import type { FieldType, SplitByReference } from '#types/System.types';

/**
 * Resolves the human-facing label for a split criterion.
 *
 * A criterion's label is what a split is "by" — e.g. "Post-Sales Line", "PO #".
 * It is used both in the split control tooltip ("Filtering form data by …") and
 * as the prefix on each split option ("Post-Sales Line: true").
 *
 * Resolution order:
 *  1. The label configured on the split itself (`description.shortLabel`),
 *     unless it is just the raw `valuePath`.
 *  2. The field type's label for the column.
 *  3. A humanized form of the `valuePath` (camelCase / snake / kebab → Title Case).
 */
const humanizeIdentifier = (raw: string): string => {
    if (raw.includes(' ')) return raw;
    const spaced = raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const resolveSplitLabel = (
    ref: Pick<SplitByReference, 'valuePath' | 'description'>,
    fieldTypes: Record<string, FieldType>,
): string => {
    const stored = ref.description?.shortLabel?.trim();
    const fieldTypeLabel = fieldTypes[ref.valuePath]?.description?.shortLabel?.trim();
    if (stored && stored !== ref.valuePath) return stored;
    if (fieldTypeLabel) return fieldTypeLabel;
    return humanizeIdentifier(ref.valuePath);
};
